import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTimes, FaBuilding } from 'react-icons/fa';
import Input from '../../components/shared/Input';
import Button from '../../components/shared/Button';
import TextArea from '../../components/shared/TextArea';

// Who an invoice is billed to. Default is the lead; "other" bills a business
// (or any other name) and keeps the lead as the contact ("Attn").
// onSubmit receives { billToName, billToAddress } — '' for both when billing the lead.

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
`;

const ModalTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${props => props.theme.colors.inputGray};
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${props => props.theme.colors.inputGray}; color: ${props => props.theme.colors.textPrimary}; }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const Choice = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  border-radius: ${props => props.theme.borderRadius};
  border: 2px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.inputGray};
  cursor: pointer;
  input { margin-top: 3px; }
`;

const ChoiceText = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  small {
    display: block;
    font-weight: 400;
    font-size: 13px;
    color: ${props => props.theme.colors.textSecondary};
    margin-top: 2px;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  margin: 16px 0 6px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: -16px;
`;

const ErrorBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: ${props => props.theme.borderRadius};
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${props => props.theme.colors.inputGray};
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CancelBtn = styled.button`
  padding: 12px 20px;
  border-radius: ${props => props.theme.borderRadius};
  border: 1px solid ${props => props.theme.colors.inputGray};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${props => props.theme.colors.background}; }
`;

const BillToModal = ({ isOpen, leadName, initial = {}, title = 'Bill To', submitLabel = 'Save', onSubmit, onCancel }) => {
  const [mode, setMode] = useState('lead');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset from the invoice's current values each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setMode(initial.billToName ? 'other' : 'lead');
    setName(initial.billToName || '');
    setAddress(initial.billToAddress || '');
    setError('');
    setSubmitting(false);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = mode === 'other'
      ? { billToName: name.trim(), billToAddress: address.trim() }
      : { billToName: '', billToAddress: '' };
    if (mode === 'other' && !payload.billToName) {
      setError('Enter the name to bill (e.g. the company name).');
      return;
    }
    try {
      setSubmitting(true);
      await onSubmit(payload);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Overlay onClick={onCancel}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalTop>
          <ModalTitle><FaBuilding /> {title}</ModalTitle>
          <CloseBtn type="button" onClick={onCancel}><FaTimes /></CloseBtn>
        </ModalTop>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            {error && <ErrorBanner>{error}</ErrorBanner>}

            <Choice $active={mode === 'lead'}>
              <input type="radio" name="billToMode" checked={mode === 'lead'} onChange={() => setMode('lead')} />
              <ChoiceText>
                {leadName || 'The lead'}
                <small>Bill the lead, as usual</small>
              </ChoiceText>
            </Choice>

            <Choice $active={mode === 'other'}>
              <input type="radio" name="billToMode" checked={mode === 'other'} onChange={() => setMode('other')} />
              <ChoiceText>
                A business / different name
                <small>{leadName || 'The lead'} stays the contact and shows as "Attn"</small>
              </ChoiceText>
            </Choice>

            {mode === 'other' && (
              <>
                <Label>Bill-to name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Company name"
                  maxLength={255}
                  autoFocus
                />
                <Label>Billing address (optional)</Label>
                <TextArea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
                <Hint>Leave blank to show only the job address.</Hint>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <CancelBtn type="button" onClick={onCancel}>Cancel</CancelBtn>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : submitLabel}
            </Button>
          </ModalFooter>
        </form>
      </ModalCard>
    </Overlay>
  );
};

export default BillToModal;
